/**
 * Betreuungsverwaltung - Hauptanwendung
 * Pengu Betreuungsassistent
 */

import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  Menu,
  X,
  Bell,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import OlympusIcon from '@/components/OlympusIcon';
import Sidebar from '@/components/Sidebar';
import GlobalSearch from '@/components/GlobalSearch';
import PWAStatus from '@/components/PWAStatus';
import BenachrichtigungsModal from '@/components/BenachrichtigungsModal';
import KlientenSchnellsuche from '@/components/KlientenSchnellsuche';
import ProfilModal from '@/components/ProfilModal';

// Drag & Drop
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Auth
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import NotificationsDropdown from '@/components/NotificationsDropdown';

// Tab System
import { TabProvider, useTabs } from '@/contexts/TabContext';
import TabBar from '@/components/TabBar';

// Pages - imported by TabRenderer, LoginPage used directly
import LoginPage from '@/pages/LoginPage';
import TabRenderer from '@/components/TabRenderer';
import DragPreviewLayer from '@/components/DragPreviewLayer';
import AssistentChat from '@/components/AssistentChat';
import ZeoPage from '@/pages/zeo/ZeoPage';

// Schnellerfassung-Imports moved to TabRenderer

import '@/App.css';

// Protected Route Component
function ProtectedRoute({ children, requiredRoles }) {
  const { isAuthenticated, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && !hasRole(requiredRoles)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [benachrichtigungenOpen, setBenachrichtigungenOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [profilOpen, setProfilOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        onOpenBenachrichtigungen={() => setBenachrichtigungenOpen(true)}
        onOpenProfil={() => setProfilOpen(true)}
      />
      
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:hidden">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-slate-600 hover:text-slate-900"
            >
              <Menu className="w-6 h-6" />
            </button>
            <span className="ml-3 font-semibold text-slate-800 flex items-center gap-2"><OlympusIcon size={24} /> Olympus</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Mobile Search Icon */}
            {isAuthenticated && (
              <button 
                onClick={() => setMobileSearchOpen(true)}
                className="p-2 text-slate-600 hover:text-slate-900"
                title="Klient suchen"
                data-testid="mobile-search-btn"
              >
                <Search className="w-5 h-5" />
              </button>
            )}
            <button 
              onClick={() => setBenachrichtigungenOpen(true)}
              className="p-2 text-slate-600 hover:text-slate-900"
              title="Benachrichtigungs-Einstellungen"
            >
              <Bell className="w-5 h-5" />
            </button>
            {isAuthenticated && <NotificationsDropdown />}
          </div>
        </header>

        {/* Mobile Search Overlay */}
        {mobileSearchOpen && (
          <div className="fixed inset-0 z-50 lg:hidden bg-black/50" onClick={() => setMobileSearchOpen(false)}>
            <div 
              className="bg-white p-4 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-5 h-5 text-slate-400" />
                <span className="font-medium text-slate-800">Klient suchen</span>
                <button 
                  onClick={() => setMobileSearchOpen(false)}
                  className="ml-auto p-1 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <KlientenSchnellsuche 
                className="w-full" 
                alwaysShowDropdown={true}
                onOpenChange={(isOpen) => {
                  // Schließe Overlay wenn Klient ausgewählt wurde
                  if (!isOpen) {
                    setMobileSearchOpen(false);
                  }
                }}
              />
              <p className="text-xs text-slate-400 mt-3">
                Tippe mindestens 2 Zeichen ein oder sieh deine Favoriten
              </p>
            </div>
          </div>
        )}

        {/* Desktop Header with Search + Notifications */}
        <header className="hidden lg:flex h-12 bg-white border-b border-slate-200 items-center justify-between px-6">
          <div className="flex items-center gap-4">
            {isAuthenticated && <GlobalSearch />}
            {isAuthenticated && <KlientenSchnellsuche />}
          </div>
          <div className="flex items-center gap-2 relative">
            {isAuthenticated && <PWAStatus />}
            {isAuthenticated && <NotificationsDropdown />}
          </div>
        </header>

        {/* Tab Bar */}
        <TabBar />

        {/* Main Content – TabRenderer hält alle Tabs gemountet */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <TabRenderer />
          </div>
        </main>
      </div>

      {/* Benachrichtigungs-Einstellungen Modal */}
      <BenachrichtigungsModal 
        isOpen={benachrichtigungenOpen}
        onClose={() => setBenachrichtigungenOpen(false)}
        benutzerId={user?.id}
      />

      {/* Profil Modal */}
      <ProfilModal
        open={profilOpen}
        onClose={() => setProfilOpen(false)}
      />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show login page
  if (!isAuthenticated && location.pathname !== '/login') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Login page without layout
  if (location.pathname === '/login') {
    return (
      <>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </>
    );
  }

  // ZEO Vollbild-Arbeitsoberflaeche (eigener Browser-Tab, eigenstaendige
  // Seite ohne Sidebar/TabBar-Chrome) -- Auth bleibt erforderlich (s.o.),
  // aber KEIN Layout/TabProvider: /zeo ist bewusst ausserhalb des
  // Tab-basierten Seitensystems, analog zum Login-Sonderfall oben.
  if (location.pathname === '/zeo') {
    return (
      <>
        <Routes>
          <Route path="/zeo" element={<ZeoPage />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <TabProvider>
        <Layout />
        <DragPreviewLayer />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            className: 'font-sans'
          }}
        />
        <AssistentChat />
      </TabProvider>
    </DndProvider>
  );
}
